import React, { useMemo, useRef, useState, useEffect } from "react";
import CategorySidebar from "../components/CategorySidebar";
import CategoryMessageInput from "../components/CategoryMessageInput";
import CategoryProjectsPanel from "../components/CategoryProjectsPanel";
import { useChatStore } from "../store/useChatStore";
import { useAuthStore } from "../store/useAuthStore";
import { SERVER_ORIGIN } from "../lib/axios";
import { Menu, X } from "lucide-react";

/* ---------- Helpers ---------- */
const humanFileSize = (bytes) => {
  if (!bytes && bytes !== 0) return "";
  const thresh = 1024,
    units = ["KB", "MB", "GB", "TB"];
  if (Math.abs(bytes) < thresh) return bytes + " B";
  let u = -1;
  do {
    bytes /= thresh;
    ++u;
  } while (Math.abs(bytes) >= thresh && u < units.length - 1);
  return bytes.toFixed(1) + " " + units[u];
};

const absFileUrl = (u = "") =>
  !u ? "" : /^https?:\/\//i.test(u) ? u : `${SERVER_ORIGIN}${u}`;

const buildDownloadUrl = (fileUrl = "", name = "") =>
  `${SERVER_ORIGIN}/api/upload/proxy-download?url=${encodeURIComponent(
    fileUrl,
  )}&name=${encodeURIComponent(name || fileUrl.split("/").pop() || "file")}`;

const pad = (n) => String(n).padStart(2, "0");
const dateKey = (ts) => {
  const d = new Date(ts);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};
const dateLabel = (ts) => {
  const d = new Date(ts);
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${pad(d.getFullYear())}`;
};

const senderIdOf = (m) =>
  m._senderId || m.senderId?._id || m.senderId?.id || m.senderId || "";

/* ---------- Linkify ---------- */
const normalizeUrl = (t = "") =>
  /^https?:\/\//i.test(t.trim()) ? t.trim() : `https://${t.trim()}`;

const linkify = (text = "") => {
  if (!text) return null;
  const rx = /((?:https?:\/\/)?(?:[a-z0-9-]+\.)+[a-z]{2,}(?:[\/?#][^\s]*)?)/gi;

  const out = [];
  let last = 0;

  String(text).replace(rx, (match, _g, idx) => {
    if (idx > last)
      out.push(<span key={idx + "-t"}>{text.slice(last, idx)}</span>);

    const url = normalizeUrl(match);
    out.push(
      <a
        key={idx + "-a"}
        href={url}
        target="_blank"
        rel="noreferrer"
        style={{ color: "#065fd4" }}
      >
        {match}
      </a>,
    );

    last = idx + match.length;
    return match;
  });

  if (last < text.length)
    out.push(<span key={"tail"}>{text.slice(last)}</span>);

  return out;
};

const highlightMentions = (text = "") => {
  const mentionRegex = /@([a-zA-Z0-9_]+)/g;

  return text.split(mentionRegex).map((part, i) => {
    if (i % 2 === 1) {
      return (
        <span
          key={i}
          style={{
            color: "#1976d2",
            fontWeight: 600,
          }}
        >
          @{part}
        </span>
      );
    }
    return <span key={i}>{part}</span>;
  });
};

/* ---------- NEW RESPONSIVE STYLES ---------- */
const S = {
  outer: {
    height: "calc(100vh - 64px)",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    background: "#f8f9fd",
    width: "100%",
  },

  shell: {
    display: "grid",
    gridTemplateColumns: "320px 1fr 280px",
    height: "100%",
    minHeight: 0,
    width: "100%",
    overflow: "hidden",
  },

  /* MOBILE — full width chat panel */
  shellMobile: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    width: "100%",
  },

  /* Chat pane */
  pane: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    minHeight: 0,
    background: "#f8f9fd",
    overflow: "hidden",
    width: "100%",
  },

  header: {
    position: "sticky",
    top: 0,
    zIndex: 10,
    padding: "14px 16px",
    background: "#ffffff",
    borderBottom: "1px solid #e8e8ef",
    fontWeight: 700,
    fontSize: 16,
    color: "#343a76",
    display: "flex",
    alignItems: "center",
    gap: 12,
  },

  /* scrollable msg list */
  listWrap: {
    flex: 1,
    overflowY: "auto",
    overflowX: "hidden",
    padding: "14px 12px",
    background: "#f6f7fb",
    width: "100%",
    boxSizing: "border-box",
  },

  row: { display: "flex", margin: "6px 0" },
  rowMe: { justifyContent: "flex-end" },

  bubble: {
    padding: 10,
    borderRadius: 12,
    maxWidth: "80vw",
    fontSize: 14,
    lineHeight: 1.4,
    border: "1px solid transparent",
    wordBreak: "break-word",
    overflowWrap: "break-word",
  },

  bubbleMe: {
    background: "#dcf8c6",
    borderColor: "#cfe9b7",
    borderTopRightRadius: 4,
  },

  bubbleOther: {
    background: "#ffffff",
    borderColor: "#e6e6ff",
    borderTopLeftRadius: 4,
  },

  mobileSidebar: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "80%",
    maxWidth: 300,
    height: "100vh",
    background: "#fff",
    zIndex: 9999,
    borderRight: "1px solid #e8e8ef",
    transform: "translateX(-100%)",
    transition: "transform 0.25s ease",
    display: "flex",
    flexDirection: "column",
  },

  mobileSidebarOpen: { transform: "translateX(0)" },

  mobileOverlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.3)",
    zIndex: 9998,
  },
};

/* ---------- Message Bubble Component ---------- */
const MessageBubble = ({ m, isMe }) => {
  const fileUrl = m.file ? absFileUrl(m.file) : "";
  const isImage = m.file && /\.(png|jpe?g|gif|webp)$/i.test(m.file);
  const isDoc = m.file && /\.(pdf|docx?|xlsx?|pptx?)$/i.test(m.file);

  return (
    <div style={{ ...S.row, ...(isMe ? S.rowMe : {}) }}>
      <div
        style={{
          ...S.bubble,
          ...(isMe ? S.bubbleMe : S.bubbleOther),
        }}
      >
        {isImage && (
          <img
            src={fileUrl}
            alt={m.fileName}
            style={{
              maxWidth: "70vw",
              borderRadius: 10,
              marginBottom: 4,
            }}
          />
        )}

        {isDoc && (
          <a
            href={buildDownloadUrl(fileUrl, m.fileName)}
            download
            style={{ color: "#065fd4", fontSize: 14 }}
          >
            📎 {m.fileName} ({humanFileSize(m.fileSize)})
          </a>
        )}

        {m.text && <div>{highlightMentions(m.text)}</div>}

        <div
          style={{
            fontSize: 11,
            opacity: 0.6,
            textAlign: "right",
            marginTop: 3,
          }}
        >
          {new Date(m.createdAt).toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
          })}
        </div>
      </div>
    </div>
  );
};

/* ---------- MAIN PAGE ---------- */
const CategoryCommunication = () => {
  const { authUser } = useAuthStore();
  const { selectedCategory, categories, categoryMessages } = useChatStore();

  const headerTitle =
    (categories || []).find(
      (c) => String(c.category) === String(selectedCategory),
    )?.name ||
    selectedCategory ||
    "Select a category";

  const [replyTo, setReplyTo] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showSidebar, setShowSidebar] = useState(false);
  const listRef = useRef(null);

  /* Handle screen resize */
  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);

  /* Build grouped rows */
  const rows = useMemo(() => {
    const sorted = [...(categoryMessages || [])].sort(
      (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
    );

    let out = [],
      last = "";
    for (const m of sorted) {
      const k = dateKey(m.createdAt);
      if (k !== last) {
        out.push({
          _sep: true,
          key: "sep-" + k,
          label: dateLabel(m.createdAt),
        });
        last = k;
      }
      out.push({ ...m, key: m._id || `${m.createdAt}-${Math.random()}` });
    }
    return out;
  }, [categoryMessages]);

  /* Auto scroll */
  useEffect(() => {
    const el = listRef.current;
    if (el) requestAnimationFrame(() => (el.scrollTop = el.scrollHeight));
  }, [rows, selectedCategory]);

  /* RETURN UI */
  return (
    <div style={S.outer}>
      <div style={isMobile ? S.shellMobile : S.shell}>
        {/* MOBILE SIDEBAR */}
        {isMobile && (
          <>
            <div
              style={{
                ...S.mobileSidebar,
                ...(showSidebar ? S.mobileSidebarOpen : {}),
              }}
            >
              <div
                style={{
                  padding: 12,
                  borderBottom: "1px solid #e8e8ef",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <strong>Categories</strong>
                <button
                  onClick={() => setShowSidebar(false)}
                  style={{ border: "none", background: "transparent" }}
                >
                  <X size={22} />
                </button>
              </div>

              <div style={{ flex: 1, overflowY: "auto" }}>
                <CategorySidebar onSelect={() => setShowSidebar(false)} />
              </div>
            </div>

            {showSidebar && (
              <div
                onClick={() => setShowSidebar(false)}
                style={S.mobileOverlay}
              />
            )}
          </>
        )}

        {/* DESKTOP SIDEBAR */}
        {!isMobile && (
          <div
            style={{
              borderRight: "1px solid #e8e8ef",
              background: "#fff",
              height: "100%",
            }}
          >
            <CategorySidebar />
          </div>
        )}

        {/* CHAT PANEL */}
        <section style={S.pane}>
          <div style={S.header}>
            {isMobile && (
              <button
                onClick={() => setShowSidebar(true)}
                style={{
                  background: "transparent",
                  border: "none",
                  fontSize: 22,
                }}
              >
                <Menu />
              </button>
            )}
            <span>{headerTitle}</span>
          </div>

          <div ref={listRef} style={S.listWrap}>
            {!selectedCategory && (
              <div style={{ color: "#667085" }}>Pick a category to start.</div>
            )}

            {selectedCategory &&
              rows.map((row) =>
                row._sep ? (
                  <div
                    key={row.key}
                    style={{ textAlign: "center", margin: "10px 0" }}
                  >
                    <span
                      style={{
                        background: "#fff",
                        border: "1px solid #e8e8ef",
                        borderRadius: 999,
                        padding: "4px 10px",
                        fontSize: 12,
                      }}
                    >
                      {row.label}
                    </span>
                  </div>
                ) : (
                  <MessageBubble
                    key={row.key}
                    m={row}
                    isMe={String(senderIdOf(row)) === String(authUser?._id)}
                  />
                ),
              )}
          </div>

          <CategoryMessageInput
            replyTo={replyTo}
            onCancelReply={() => setReplyTo(null)}
          />
        </section>

        {/* RIGHT PANEL — DESKTOP ONLY */}
        {!isMobile && (
          <div
            style={{
              borderLeft: "1px solid #e8e8ef",
              overflow: "hidden",
            }}
          >
            <CategoryProjectsPanel selectedCategory={selectedCategory} />
          </div>
        )}
      </div>
    </div>
  );
};

export default CategoryCommunication;
